import { ENDPOINTS } from '@/shared/api';
import type {
  VtonClientErrorCode,
  VtonFinalResult,
  VtonResultResponse,
  VtonRunResponse,
} from './vton-types';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ITERATIONS = 60;
const FALLBACK_URL = '/intro/sample_output_1.jpg' as const;

export class VtonClientError extends Error {
  public code: VtonClientErrorCode;

  constructor(message: string, code: VtonClientErrorCode) {
    super(message);
    this.name = 'VtonClientError';
    this.code = code;
  }
}

export async function runJob(file: File): Promise<VtonRunResponse> {
  const form = new FormData();
  form.append('image', file);

  return postMultipart<VtonRunResponse>(
    ENDPOINTS.landing.vton.run(),
    form,
    isVtonRunResponse,
  );
}

export async function pollResult(
  jobId: string,
  requestId: string,
  signal?: AbortSignal,
): Promise<VtonFinalResult> {
  for (let attempt = 0; attempt < MAX_POLL_ITERATIONS; attempt += 1) {
    if (signal?.aborted) {
      throw new VtonClientError('Polling was aborted', 'NETWORK');
    }

    const result = await getJson<VtonResultResponse>(
      ENDPOINTS.landing.vton.results(jobId),
      isVtonResultResponse,
      signal,
    );

    if (result.status === 'completed') {
      return {
        kind: 'completed',
        image: result.image,
        imageContentType: result.imageContentType,
      };
    }

    if (result.status === 'failed') {
      const errorMessage =
        (result.error && typeof result.error.message === 'string'
          ? result.error.message
          : null) ?? 'VTON processing failed';
      return { kind: 'failed', error: errorMessage };
    }

    if (attempt < MAX_POLL_ITERATIONS - 1) {
      await delay(POLL_INTERVAL_MS, signal);
    }
  }

  return { kind: 'timeout', requestId, fallbackUrl: FALLBACK_URL };
}

async function postMultipart<T>(
  url: string,
  form: FormData,
  guard: (value: unknown) => value is T,
): Promise<T> {
  // Content-Type 헤더를 직접 설정하지 않는다 — 브라우저가 multipart boundary를 채운다.
  const response = await requestWithoutJson(url, {
    method: 'POST',
    body: form,
  });

  return parseJsonResponse<T>(response, guard);
}

async function getJson<T>(
  url: string,
  guard: (value: unknown) => value is T,
  signal?: AbortSignal,
): Promise<T> {
  const response = await requestWithoutJson(url, { method: 'GET', signal });

  return parseJsonResponse<T>(response, guard);
}

async function requestWithoutJson(
  url: string,
  init: RequestInit,
): Promise<Response> {
  let response: Response;

  try {
    response = await fetch(url, init);
  } catch (error: unknown) {
    if (error instanceof VtonClientError) {
      throw error;
    }

    throw new VtonClientError('VTON request failed', 'NETWORK');
  }

  if (response.status >= 400 && response.status < 500) {
    throw new VtonClientError(
      'VTON request returned a client error',
      'HTTP_4XX',
    );
  }

  if (response.status >= 500) {
    throw new VtonClientError(
      'VTON request returned a server error',
      'HTTP_5XX',
    );
  }

  return response;
}

async function parseJsonResponse<T>(
  response: Response,
  guard: (value: unknown) => value is T,
): Promise<T> {
  let value: unknown;

  try {
    value = await response.json();
  } catch {
    throw new VtonClientError(
      'VTON response was not valid JSON',
      'INVALID_RESPONSE',
    );
  }

  if (!guard(value)) {
    throw new VtonClientError(
      'VTON response shape was invalid',
      'INVALID_RESPONSE',
    );
  }

  return value;
}

async function delay(
  milliseconds: number,
  signal?: AbortSignal,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new VtonClientError('Polling was aborted', 'NETWORK'));
      return;
    }

    const timeoutId = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeoutId);
        reject(new VtonClientError('Polling was aborted', 'NETWORK'));
      },
      { once: true },
    );
  });
}

function isVtonRunResponse(value: unknown): value is VtonRunResponse {
  return (
    isRecord(value) &&
    typeof value.jobId === 'string' &&
    value.status === 'pending' &&
    typeof value.requestId === 'string' &&
    typeof value.expiresAt === 'string'
  );
}

function isVtonResultResponse(value: unknown): value is VtonResultResponse {
  if (!isRecord(value) || typeof value.jobId !== 'string') {
    return false;
  }

  if (value.status === 'pending' || value.status === 'processing') {
    return true;
  }

  if (value.status === 'completed') {
    return (
      typeof value.image === 'string' &&
      typeof value.imageContentType === 'string'
    );
  }

  if (value.status === 'failed') {
    return value.error === null || isRecord(value.error);
  }

  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
